import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ImageViewerService, ImageViewerItem } from './image-viewer.service';

@Component({
  selector: 'app-image-viewer',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss']
})
export class ImageViewerComponent implements OnInit, OnDestroy {
  visible = false;
  images: ImageViewerItem[] = [];
  currentIndex = 0;
  zoom = 1;
  rotation = 0;
  translateX = 0;
  translateY = 0;

  private dragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartTranslateX = 0;
  private dragStartTranslateY = 0;
  private sub!: Subscription;

  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 5;
  readonly ZOOM_STEP = 0.25;

  constructor(
    private viewerService: ImageViewerService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.sub = this.viewerService.open$.subscribe(req => {
      this.images = req.images;
      this.currentIndex = req.startIndex;
      this.resetTransform();
      this.visible = true;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get currentImage(): ImageViewerItem | null {
    return this.images[this.currentIndex] ?? null;
  }

  get imageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.zoom}) rotate(${this.rotation}deg)`;
  }

  close(): void {
    this.visible = false;
    this.cdr.markForCheck();
  }

  zoomIn(): void {
    this.zoom = Math.min(this.MAX_ZOOM, +(this.zoom + this.ZOOM_STEP).toFixed(2));
    this.cdr.markForCheck();
  }

  zoomOut(): void {
    this.zoom = Math.max(this.MIN_ZOOM, +(this.zoom - this.ZOOM_STEP).toFixed(2));
    if (this.zoom <= 1) {
      this.translateX = 0;
      this.translateY = 0;
    }
    this.cdr.markForCheck();
  }

  rotateRight(): void {
    this.rotation = (this.rotation + 90) % 360;
    this.cdr.markForCheck();
  }

  rotateLeft(): void {
    this.rotation = (this.rotation - 90 + 360) % 360;
    this.cdr.markForCheck();
  }

  prev(): void {
    if (this.images.length <= 1) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.resetTransform();
    this.cdr.markForCheck();
  }

  next(): void {
    if (this.images.length <= 1) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.resetTransform();
    this.cdr.markForCheck();
  }

  resetTransform(): void {
    this.zoom = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('image-viewer-backdrop')) {
      this.close();
    }
  }

  onMouseDown(event: MouseEvent): void {
    if (this.zoom <= 1) return;
    event.preventDefault();
    this.dragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartTranslateX = this.translateX;
    this.dragStartTranslateY = this.translateY;
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragging) return;
    this.translateX = this.dragStartTranslateX + (event.clientX - this.dragStartX);
    this.translateY = this.dragStartTranslateY + (event.clientY - this.dragStartY);
    this.cdr.markForCheck();
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    this.dragging = false;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (!this.visible) return;
    switch (event.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.prev();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case '+':
      case '=':
        this.zoomIn();
        break;
      case '-':
        this.zoomOut();
        break;
      case 'r':
        this.rotateRight();
        break;
    }
  }
}
